'use client';

import { type ReactNode, useRef } from 'react';

import { RailScrollButtons, useRailScroll } from '@ps/ui/molecules/RailScroll';

interface Props {
  /** The scroll-container className (the shared `CLIP_RAIL_CLASSES`). */
  className: string;
  /** Number of clips — re-measures overflow when it changes. */
  itemCount: number;
  children: ReactNode;
}

/**
 * Client leaf for the static My Life clip rail: a native horizontal scroller
 * with prev/next chevrons that appear only when the clips overflow the row.
 * Overflow tracking + the chevrons live in {@link useRailScroll} /
 * {@link RailScrollButtons}, shared with the CollectionButton/series rail so the
 * arrow logic isn't duplicated. Kept as a small `'use client'` island so the
 * surrounding `SeriesCardView` stays server-renderable.
 */
export function HorizontalClipRail({ className, itemCount, children }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { scrollByDirection, ...scroll } = useRailScroll(
    scrollerRef,
    itemCount,
  );

  return (
    <div className="relative">
      <div ref={scrollerRef} className={className}>
        {children}
      </div>
      <RailScrollButtons scroll={scroll} onScroll={scrollByDirection} />
    </div>
  );
}
